<template>
    <div class="stack-slider" :class="[classModifier ? `stack-slider--${classModifier}` : '']">
        <carousel-3d
            :autoplay="isAutoplay"
            :autoplayTimeout="2500"
            :perspective="0"
            :display="3"
            :border="0"
            :dir="'ltr'"
            :controlsVisible="!isPhone && !isFrame"
            :controlsPrevHtml="'&#8592;'"
            :controlsNextHtml="'&#8594;'"
            @before-slide-change="onBeforeSlideChange"
            ref="carousel">
            <template v-if="isFrame">
                <slide v-for="(image, index) in images" :index="index" :key="index">
                    <div class="iphone iphone--portrait">
                        <div class="iphone__nest">
                            <div class="iphone__video-holder">
                                <div class="iphone__frame">
                                    <template v-if="isVideo(image)">
                                        <video
                                            :src="getVideoSrc(image)"
                                            :autoplay="current === index"
                                            :muted="true"
                                            :loop="true"
                                            :playsinline="true"
                                            loading="lazy"
                                            @loadedmetadata="handleVideoLoad($event, index)" />
                                    </template>
                                    <template v-else>
                                        <picture v-if="isDesktop">
                                            <source
                                                media="(max-width: 480px)"
                                                :srcset="`${image[0]} 1x, ${image[1]} 2x`" />
                                            <img :src="image[2]" :srcset="`${image[3]} 2x`" alt="img" loading="lazy" />
                                        </picture>
                                        <img
                                            :src="image[0]"
                                            :srcset="`${image[1]} 2x`"
                                            alt="img"
                                            loading="lazy"
                                            v-else />
                                    </template>
                                    <div class="iphone__frame-inner"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </slide>
            </template>
            <template v-else>
                <slide v-for="(image, index) in images" :index="index" :key="index">
                    <template v-if="isVideo(image)">
                        <video
                            :src="getVideoSrc(image)"
                            :autoplay="current === index + 1"
                            :muted="true"
                            :loop="true"
                            :playsinline="true"
                            loading="lazy"
                            @loadedmetadata="handleVideoLoad($event, index)" />
                    </template>
                    <template v-else>
                        <picture v-if="isDesktop">
                            <source media="(max-width: 480px)" :srcset="`${image[0]} 1x, ${image[1]} 2x`" />
                            <img :src="image[2]" :srcset="`${image[3]} 2x`" alt="img" loading="lazy" />
                        </picture>
                        <img :src="image[0]" :srcset="`${image[1]} 2x`" alt="img" loading="lazy" v-else />
                    </template>
                </slide>
            </template>
        </carousel-3d>

        <i v-if="captions[current]" class="caption" v-html="captions[current]"></i>

        <div class="stack-slider__pagination">
            <div
                v-for="(item, i) in 3"
                class="stack-slider__pagination-item"
                :class="{ 'stack-slider__pagination-item--active': current === i }"
                :key="i"
                @click="goToSlide(i)"></div>
        </div>
    </div>
</template>

<script>
import { Carousel3d, Slide } from '@nanoandrew4/vue3-carousel-3d';
import { helpers } from '../helpers';

export default {
    components: {
        Carousel3d,
        Slide,
    },
    props: {
        classModifier: {
            type: String,
            required: false,
            default: '',
        },
        frame: {
            type: String,
            required: true,
        },
        images: {
            type: Array,
            required: true,
        },
        desktop: {
            type: String,
            required: false,
            default: 'false',
        },
        autoplay: {
            type: String,
            required: false,
            default: 'false',
        },
        captions: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            current: 0,
            videoRefs: [],
        };
    },
    computed: {
        isPhone() {
            return helpers.isPhone();
        },
        isFrame() {
            return this.frame === 'true';
        },
        isDesktop() {
            return this.desktop === 'true';
        },
        isAutoplay() {
            return this.autoplay === 'true';
        },
    },
    methods: {
        goToSlide(index) {
            this.$refs.carousel.goSlide(index);
        },
        onBeforeSlideChange(index) {
            // Pause all videos
            this.pauseAllVideos();
            this.current = index;
            // Play current video if it exists
            this.$nextTick(() => {
                this.playCurrentVideo();
            });
        },
        isVideo(item) {
            return item && typeof item === 'object' && item.type === 'video';
        },
        getVideoSrc(item) {
            if (this.isVideo(item)) {
                return item.src || item.url;
            }
            return '';
        },
        handleVideoLoad(event, index) {
            this.videoRefs[index] = event.target;
            if (index === this.current + 1) {
                event.target.play();
            }
        },
        pauseAllVideos() {
            this.videoRefs.forEach((video) => {
                if (video) {
                    video.pause();
                }
            });
        },
        playCurrentVideo() {
            const currentVideo = this.videoRefs[this.current + 1];
            if (currentVideo) {
                currentVideo.play();
            }
        },
    },
};
</script>

<style lang="sass">
.stack-slider
    padding: 0

    .caption
       margin-top: 100px

    &.dma
        @media screen and (min-width: 768px) and (orientation: portrait)
            padding: 0

        @media screen and (min-width: 1024px)
            padding: 0

        .carousel-3d-container
            overflow: visible

        &.stack-slider
            .carousel-3d-slider
                transform: translateX(-28px)

            .carousel-3d-slider
                @media screen and (min-width: 568px)
                    width: 725px !important
                    height: 600px !important
                    transform: none

            .carousel-3d-slide
                @media screen and (min-width: 568px)
                    width: 625px !important
                    height: 500px !important

        &.stack-slider--open
            .carousel-3d-slide
                &.current
                    transform: translate(28px, 28px) !important

                    @media screen and (min-width: 568px)
                        transform: translate(50px, 50px) !important

                &.left-1
                    transform: translate(56px, 56px) !important

                    @media screen and (min-width: 568px)
                        transform: translate(100px, 100px) !important

                &.right-1
                    transform: none !important

        .carousel-3d-controls
            @media (pointer: coarse)
                display: none

            .prev
                left: 0px

            .next
                right: 0px

        .stack-slider__pagination
            @media (pointer: coarse)
                display: flex

    &__pagination
        display: flex
        align-items: flex-start
        justify-content: center
        gap: 16px
        margin: 24px 0 0 0

        @media screen and (min-width: 768px) and (orientation: portrait)
            display: none

        @media screen and (min-width: 1024px)
            display: none

    &__pagination-item
        height: 9px
        width: 9px
        border-radius: 50%
        background: #707070
        transition: background-color .2s ease-in-out

        &--active
            background: #D9D9D9

    .carousel-3d-container
        height: 534px !important
        cursor: grab

        &:focus
            cursor: grabbing

    .carousel-3d-slider
        height: 534px !important
        width: 224px !important
        transform-style: unset !important
        perspective: none !important

    .carousel-3d-slide
        height: 456px !important
        width: 224px !important
        padding: 0
        background: none
        overflow: visible
        will-change: transform
        transform: none !important
        transition: transform 500ms ease
        position: relative

        &::after
            content: ''
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            background: rgba(0, 0, 0, 0.3)
            pointer-events: none
            transition: opacity 500ms ease
            z-index: 1

        &.left-1
            z-index: 1000 !important

            &::after
                opacity: 0

        img,
        video
            height: 100%
            object-fit: cover
            transform: none !important

    &--open
        &.stack-slider--open
            .carousel-3d-slide
                &.current
                    transform: translate(0, 30px) !important

                &.left-1
                    transform: translate(-60px, 0) !important

                &.right-1
                    transform: translate(60px, 60px) !important

    &--random
        // @media screen and (min-width: 1024px)
        //     padding: 0 73px 0 0

        .carousel-3d-container
            height: 100% !important
            margin: 0 auto !important

        .carousel-3d-slider
            height: 443px !important
            width: 287px !important

            @media screen and (min-width: 1024px)
                height: 564px !important
                width: 464px !important

        .carousel-3d-slide
            height: 383px !important
            width: 287px !important

            @media screen and (min-width: 1024px)
                height: 500px !important
                width: 400px !important

        &.stack-slider--open
            .carousel-3d-slide
                &.current
                    transform: translate(30px, 30px) !important

                    @media screen and (min-width: 1024px)
                        transform: translate(32px, 32px) !important

                &.left-1
                    transform: translate(0, 60px) !important

                    @media screen and (min-width: 1024px)
                        transform: translate(64px, 64px) !important

                &.right-1
                    transform: translateX(-30px) !important

                    @media screen and (min-width: 1024px)
                        transform: none !important

        .stack-slider__pagination-item
            background: #D9D9D9

            &--active
                background: #707070

    .carousel-3d-controls
        @media screen and (max-width: 480px) and (orientation: portrait)
            display: none

        .prev,
        .next
            display: flex
            align-items: center
            justify-content: center
            height: 62px
            width: 62px
            font-size: 28px
            border-radius: 50%
            background: linear-gradient(139.81deg, rgba(235, 237, 254, 0.6) 13.11%, rgba(235, 237, 254, 0.1) 84.95%)
            box-shadow: 4px 4px 21px -1px rgba(0, 0, 0, 0.25)
            backdrop-filter: blur(20px)

            &:hover
                opacity: 1

        .prev
            left: auto
            right: 30px
            transform: rotate(180deg)

        .next
            left: 30px
            transform: rotate(180deg)
</style>
